import React, { createContext, useContext, useEffect, useState } from "react";
import { onAuthStateChanged, signInWithPopup, signOut, sendPasswordResetEmail } from "firebase/auth";
import { auth, googleProvider, getUserRole, bootstrapAdminRole, setUserRole, processReferral } from "../firebase"; // eslint-disable-line no-unused-vars

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(undefined);    // undefined = loading
  const [userRole, setUserRole] = useState(null); // { role, channelIds, email, ... }
  const [authError, setAuthError] = useState(null);


  // Capture referral code from URL before auth popup clears it
  useEffect(() => {
    const ref = new URLSearchParams(window.location.search).get('ref');
    if (ref) sessionStorage.setItem('cc_pending_ref', ref);
  }, []);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      if (!u) { setUser(null); setUserRole(null); return; }

      // Try bootstrap for hardcoded admins (no-op if role already exists)
      await bootstrapAdminRole(u.email);

      // Load role from Firestore
      const role = await getUserRole(u.email);

      if (!role) {
        // No role doc — auto-create as owner so they can add their own channel
        const newRole = {
          email: u.email,
          role: "owner",
          channelIds: [],
          displayName: u.displayName || u.email.split("@")[0],
          plan: "free",
          ai_credits_total: 25,
          ai_credits_remaining: 25,
          created_at: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };
        await setUserRole(u.email, "owner", [], newRole.displayName);
        setUser(u);
        setUserRole(newRole);
        setAuthError(null);
        return;
      }

      setUser(u);
      setUserRole(role);
      setAuthError(null);

      // Process any pending referral code
      const pendingRef = sessionStorage.getItem('cc_pending_ref');
      if (pendingRef && role && !role.referred_by) {
        processReferral(u.email, pendingRef).then(() => {
          sessionStorage.removeItem('cc_pending_ref');
        });
      }

    });
    return unsub;
  }, []);

  const login = async () => {
    setAuthError(null);
    try {
      await signInWithPopup(auth, googleProvider);
    } catch (e) {
      if (e.code !== "auth/popup-closed-by-user") {
        setAuthError("Sign-in failed. Please try again.");
      }
    }
  };

  const logout = () => {
    setUser(null);
    setUserRole(null);
    signOut(auth);
  };

  const resetPassword = async (email) => {
    try {
      await sendPasswordResetEmail(auth, email);
      return { success: true };
    } catch(e) {
      return { success: false, error: e.message };
    }
  };

  // Convenience role checkers
  const isAdmin = userRole?.role === "admin";
  const isOwner = userRole?.role === "owner";
  const isUser  = userRole?.role === "user";
  const canWrite = isAdmin || isOwner;

  // Which channel IDs this user can access
  // Admins see everything (channelIds filtering happens in useChannel)
  const allowedChannelIds = isAdmin ? null : (userRole?.channelIds || []);

  return (
    <AuthContext.Provider value={{
      user, userRole, authError,
      resetPassword,
      loading: user === undefined,
      login, logout,
      isAdmin, isOwner, isUser, canWrite,
      allowedChannelIds,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
